/**
 * ⭐ `AW18` + `AW23` + `AW26` · **Elegir a quién se impersona.**
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ RASTRO `[28/8/2026]` — `AW26`: LA PREMISA DE `AW18` ERA FALSA
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Lo de abajo dice, y decía bien para lo que se sabía, que el participante «sale del
 * padrón». **`ADR-0232` midió que el motor NUNCA consulta el padrón**:
 * `recibir.ts:823` hace `deps.participants.find(arm, from)` contra `directorio`, que
 * `composicion.ts:615-616` declara textual como *«un `Map` armado desde el `.env`»*
 * (`F7_PARTICIPANTS`), porque **`modules/participants` no existe**. ⇒ **elegir
 * cualquier fila del buscador daba `emisor-desconocido`, siempre, por
 * construcción.**
 *
 * ⭐ **Es un ① de `examinar` —la fuente no alcanzaba—, no un desvío del
 * implementador**, y por eso `AW18` se ENMIENDA en su lugar con el rastro
 * (`ADR-0082`) y su argumento se cita en vez de borrarse.
 *
 * ⇒ **conviven TRES caminos, y ninguno reemplaza a los otros:**
 *
 *   ① **el selector de ENROLADOS** (`config.enrolados`, derivado de
 *      `F7_PARTICIPANTS` en el entrypoint con la misma mecánica que `canales`,
 *      `ADR-0231`) — **el que anda**;
 *   ② **el buscador del padrón** — degradado a ESCAPE, **no borrado y no
 *      deshabilitado**: es el que produce `emisor-desconocido` **a pedido**;
 *   ③ **la dirección a mano** (vive en `chat.tsx`) — el control negativo crudo.
 *
 * ⛔⛔ **② NO SE DESHABILITA, Y ESO NO ES UNA CONCESIÓN.** Es la misma regla que
 * `AW18` fijó para el `<input>` de texto libre, con su argumento intacto: *«si el
 * selector fuera el único camino, ese desenlace se volvería inalcanzable desde la
 * pantalla y la etapa habría reducido el instrumento mientras lo hacía más
 * cómodo»*. Se **informa** —una fila que no está en `config.enrolados` se marca
 * como **no enrolada**, con esas palabras, ANTES de mandar nada— y **no se
 * bloquea**: la misma disciplina que `AW19` fijó para la ventana vencida.
 *
 * ⚠️ **`config.enrolados` AUSENTE es legítimo y va a ocurrir**: la guarda de PII del
 * entrypoint omite la clave entera si alguna dirección tiene forma de teléfono. ⇒ el
 * selector de ① **no se dibuja** —no un `<select>` vacío, no un participante
 * inventado—, quedan ② y ③, y **tampoco se marca a nadie como no enrolado**: sin la
 * lista, esa afirmación no se puede hacer.
 *
 * ⛔ **El `brazo` del enrolado es un DATO.** No elige el canal y no filtra la lista
 * de canales: `bot-web` deriva el brazo del canal receptor.
 *
 * ⛔⛔ **REUSA `borde-padron.ts` ENTERO Y NO ESTRENA NINGÚN CABLE.** Un borde propio
 * del simulador sería una **segunda declaración del mismo cable**: el día que
 * `admin-web` renombre un parámetro, la pantalla del padrón se rompe y ésta **sigue
 * compilando y deja de andar** — el mismo hueco que `borde-simulador.ts` §② declara
 * contra `bot-web` y que acá no hay que volver a pagar.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⭐⭐ `AW23` · UN SOLO CAMPO, DOS CONSULTAS · `ADR-0228`
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `GET padron` expone `nombre` y `telefono` como parámetros **SEPARADOS** y no hay
 * un `q`. ⇒ lo tipeado se manda a **los dos**, en paralelo, y los resultados se
 * fusionan por `id`.
 *
 * ⛔⛔ **LA HEURÍSTICA ESTÁ PROHIBIDA, Y NO POR ELEGANCIA.** «Si parece número,
 * buscá por dirección» es una línea, se lee razonable, y **falla callada**:
 *
 *   · un nombre que empiece con un dígito     → cero filas
 *   · una dirección con espacios o guiones     → cero filas
 *   · una dirección que NO es telefónica       → cero filas
 *
 * y ese último caso **es exactamente la forma de este ambiente**: los participantes
 * que el motor acepta son `dir-qa-puro-001` y `dir-qa-hib-002` —medido el
 * 28/8/2026—, así que la heurística telefónica **ni siquiera aplica**.
 *
 * ⇒ **el síntoma de los tres es «no hay nadie con ese filtro»**, que es lo mismo que
 * la pantalla dice cuando el padrón está vacío. **El operador no puede distinguir
 * «no existe» de «lo busqué en el campo equivocado»**, y va a ir a mirar el padrón,
 * la ingesta o el brazo antes de sospechar del buscador.
 *
 * ⛔ **Y no hay filtro por lote.** Acá se impersona a UNA persona, no se arma una
 * audiencia: el lote no acota ninguna decisión de esta pantalla, y el ambiente
 * medido tiene veinticuatro. `AW15` lo conserva, porque allá sí acota.
 *
 * ⛔⛔ **EL COSTO DE PII, DICHO ACÁ Y NO EN UNA NOTA AL PIE.** El identificador con
 * el que se impersona ES la dirección: no se puede elegir a alguien sin verla.
 * `borde-simulador.ts:65` declara que la pantalla **no dibuja** el `destinatario` de
 * una entrega, y esa política **sigue en pie para el hilo**; lo que cambia acá, con
 * la decisión del dueño del 28/8/2026, es el SELECTOR. ⇒ la lista vive detrás de la
 * puerta de `ADR-0226`.
 *
 * ⛔ **No da de alta a nadie y no inventa a nadie.** El alta es `AW7`, el padrón es
 * `AW15`.
 */

import { useEffect, useRef, useState } from "react"
import type { JSX } from "react"
import type { BordePadron, ConsultaDelPadron, FilaDelPadron } from "../padron/borde-padron.ts"

/**
 * Lo que el selector devuelve al elegir. `null` limpia la elección.
 *
 * ⛔⭐ **RASTRO `[28/8/2026]` — `telefono` SE LLAMA `direccion`, Y NO ES COSMÉTICO.**
 * `ADR-0232` midió que hay **dos registros** con **dos identificadores distintos**:
 * el padrón identifica por `telefono` y el motor por `direccion` (el 3.º campo de
 * `F7_PARTICIPANTS`). Lo que viaja como `de` al `POST /entrada` **es siempre una
 * dirección** —Meta manda una dirección—, así que un campo llamado `telefono`
 * desembocando en `de` es exactamente la confusión que produjo el defecto.
 */
export type Elegido = {
  /**
   * El id del PADRÓN (`participants.participants`).
   */
  readonly id: string
  /** ⭐ **Lo que viaja como `de`.** Nunca el `id`. Ver `ADR-0232` y el `guard` de `AW26`. */
  readonly direccion: string
  /** Cómo se lo nombra en pantalla. */
  readonly nombre: string
  /**
   * El brazo, **como DATO**. `null` cuando no se sabe.
   *
   * ⛔ **No elige el canal y no filtra la lista de canales.** `bot-web` deriva el
   * brazo del canal receptor y contesta `canal-no-ruteable` si no rutea.
   */
  readonly brazo: string | null
} | null

/**
 * Lo que se espera entre teclas antes de consultar.
 *
 * ⚠️ **No sale de ninguna fuente y se elige acá**, igual que `AW16` reportó para su
 * intervalo de sondeo. Se declara como hueco en vez de esconderlo: el número
 * correcto depende de la latencia de `admin-web` sobre ~16.000 filas, que **nadie
 * midió**. Dueño: `web-dev` con el TL.
 */
const ESPERA_MS = 300

/** El tope de filas que se dibujan. Más que esto no se elige a ojo: se afina. */
const TOPE_VISIBLE = 12

/**
 * ⛔⛔ **`orden` ES REQUERIDO POR EL CONTRATO Y `ConsultaDelPadron` TODAVÍA NO LO
 * DECLARA — EL CAMPO SE DECLARA ACÁ, EN EL ÚNICO LUGAR QUE LO MANDA.**
 *
 * `contracts/participants/index.ts:1454` (`R3-CPA2`, B27) hace `orden` REQUERIDO
 * (`MasterPageQuery.orden`), y el acceptance de `R3-UI6` manda el default
 * explícito (`null` = lote más reciente → nombre → teléfono). Pero
 * `ConsultaDelPadron` (`src/padron/borde-padron.ts:103`) se quedó sin el campo:
 * cerrarlo es de la etapa del orden del padrón, y **su territorio no es éste**
 * (`apps/management-ui/src/simulador/**`): pedirlo acá sería tocar el borde de
 * otra etapa, que es lo que el perímetro prohíbe.
 *
 * ⇒ el campo se declara en ESTE archivo, con una intersección sobre el tipo del
 * borde, y la consulta se pasa como variable (no como literal suelto). El valor
 * —`null`— es el del contrato y viaja DENTRO del objeto de la consulta, que es lo
 * que el gate exige (M12): una anotación suelta en otra parte del archivo no salva
 * a una consulta que lo omite. El día que `borde-padron.ts` declare `orden`, este
 * alias se borra sin tocar el resto.
 */
type ConsultaDelPadronConOrden = ConsultaDelPadron & { readonly orden: null }

export function SelectorParticipante({
  borde,
  elegido,
  onElegir,
}: {
  readonly borde: BordePadron
  readonly elegido: Elegido
  readonly onElegir: (e: Elegido) => void
}): JSX.Element {
  const [q, setQ] = useState("")
  const [filas, setFilas] = useState<readonly FilaDelPadron[]>([])
  const [buscando, setBuscando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [buscado, setBuscado] = useState(false)

  /**
   * ⭐ El número de la consulta en vuelo. **Una respuesta vieja no pisa a una
   * nueva**: sin esto, una consulta lenta de hace tres teclas escribe sus
   * resultados encima de los de la tecla actual y el operador ve una lista que **no
   * corresponde a lo que tiene escrito**. Es la misma disciplina que el `vivo` del
   * sondeo de `AW16`.
   */
  const turno = useRef(0)

  useEffect(() => {
    const consulta = q.trim()
    if (consulta === "") {
      setFilas([])
      setBuscado(false)
      setError(null)
      return undefined
    }

    const mio = ++turno.current
    const reloj = setTimeout(() => {
      setBuscando(true)
      /*
       * ⭐⭐ LAS DOS CONSULTAS, EN PARALELO. `ADR-0228`. Sin heurística no hay lado
       * equivocado: lo tipeado se prueba contra los dos campos y lo que aparezca en
       * cualquiera entra.
       *
       * ⛔⭐ EL `tamanoDePagina` ES EL LITERAL `10`, Y NO SALE DE `config.pageSize`.
       * `GET padron` sólo acepta los cinco del contrato (`PARTICIPANTES_PAGE_SIZES`,
       * `contracts/participants/index.ts`): {10, 50, 100, 200, 500}. El día que la
       * instancia ponga `pageSize = 7`, el padrón responde 400 (R3-AW4) y el síntoma
       * se confunde con «el padrón está vacío». Por eso va el literal, elegido acá y
       * no heredado de una variable de entorno.
       *
       * ⛔ Y `orden: null` VIAJA DENTRO DEL OBJETO y vale el default del contrato
       * (`orden` es REQUERIDO, `contracts/participants/index.ts:1434-1454`; `null` =
       * lote más reciente → nombre → teléfono). Una anotación suelta en otra parte
       * del archivo no salva a una consulta que lo omite.
       */
      // Las DOS consultas salen acá adentro, en paralelo (ADR-0228): una por
      // teléfono y otra por nombre, cada una con el literal del contrato y el
      // orden por defecto explícito. Se declaran como variables del tipo
      // `ConsultaDelPadronConOrden` (arriba) para que `orden` viaje DENTRO del
      // objeto sin que `tsc` lo lea como propiedad de más: el borde todavía no
      // lo declara y ensancharlo es de otra etapa.
      const consultaPorNombre: ConsultaDelPadronConOrden = { filtro: { lotes: [], nombre: consulta, telefono: null }, pagina: 1, tamanoDePagina: 10, orden: null }
      const consultaPorDireccion: ConsultaDelPadronConOrden = { filtro: { lotes: [], nombre: null, telefono: consulta }, pagina: 1, tamanoDePagina: 10, orden: null }

      Promise.all([borde.pagina(consultaPorNombre), borde.pagina(consultaPorDireccion)])
        .then(([porNombre, porDireccion]) => {
          if (mio !== turno.current) return
          // Fusión por `id`, orden estable: primero los que matchean por nombre.
          const porId = new Map<string, FilaDelPadron>()
          for (const f of [...porNombre.items, ...porDireccion.items]) {
            if (!porId.has(f.id)) porId.set(f.id, f)
          }
          setFilas([...porId.values()].slice(0, TOPE_VISIBLE))
          setError(null)
          setBuscado(true)
        })
        .catch((causa: unknown) => {
          if (mio !== turno.current) return
          setError(causa instanceof Error ? causa.message : String(causa))
          setFilas([])
          setBuscado(true)
        })
        .finally(() => {
          if (mio === turno.current) setBuscando(false)
        })
    }, ESPERA_MS)

    return () => clearTimeout(reloj)
  }, [q, borde])

  return (
    <section className="sim__lista" aria-labelledby="sim-selector-titulo">
      <h2 id="sim-selector-titulo" className="sim__rotulo">
        Con quién se escribe
      </h2>

      {elegido !== null ? (
        <div className="sim__elegido" aria-live="polite">
          <span className="sim__elegido-nombre">{elegido.nombre}</span>
          <code className="sim__elegido-dir">{elegido.direccion}</code>
          {/*
            ⛔ El brazo del enrolado se muestra como DATO y nada más: no elige el
            canal y no filtra la lista de canales. El brazo lo deriva `bot-web` del
            canal receptor, y contesta `canal-no-ruteable` si no rutea.
          */}
          {elegido.brazo !== null && (
            <span className="sim__elegido-brazo">brazo {elegido.brazo}</span>
          )}
          <button type="button" className="boton boton--chico" onClick={() => onElegir(null)}>
            Cambiar
          </button>
        </div>
      ) : (
        <>
          <div className="sim__campo">
            {/* ⛔ Sin `placeholder` y sin ejemplo: el formato se DESCRIBE en palabras. */}
            <label htmlFor="sim-buscar">
              Buscar en el padrón por nombre o dirección
            </label>
            <input
              id="sim-buscar"
              type="search"
              autoComplete="off"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-describedby="sim-buscar-ayuda"
            />
            <p id="sim-buscar-ayuda" className="sim__ayuda">
              {/*
                ⛔⭐ ESTO NO ES UN DETALLE DE COPY: es la semántica REAL del cable,
                medida el 28/8/2026 contra `admin-web`, y sin decirla la pantalla
                miente por omisión.
                  nombre   → PREFIJO  («Part» da 145 filas; «articipante» da 0)
                  telefono → EXACTO   una dirección a la que le falte el último
                                      dígito da 0; la misma completa da 1
                ⛔ Y el formato NO se ilustra con un número: `gates/pii.spec.ts`
                   barre TODO el árbol —comentarios incluidos, y con razón: para
                   PII la presencia ES el problema, no el uso— y esta misma línea
                   lo puso en rojo el 28/8/2026 por escribir uno de ejemplo.
                Un operador que tipee un pedazo del medio del nombre, o los últimos
                dígitos de una dirección, recibe «nadie coincide» — y eso ya
                significa otra cosa en esta pantalla.
              */}
              Se consulta por los dos campos a la vez, en todos los lotes.{" "}
              <strong>El nombre busca desde el principio</strong> y{" "}
              <strong>la dirección tiene que ir completa</strong>: así lo resuelve{" "}
              <code>admin-web</code>, no esta pantalla.{" "}
              <strong>
                Las direcciones sintéticas dir-qa-* del banco se encuentran por NOMBRE
              </strong>
              : el filtro Teléfono las limpia y devuelve 0 filas.
            </p>
          </div>

          {buscando && (
            <p className="sim__ayuda" role="status">
              Buscando…
            </p>
          )}

          {error !== null && (
            <p className="simulador__error" role="alert">
              No se pudo leer el padrón: {error}
            </p>
          )}

          {buscado && !buscando && filas.length === 0 && error === null && (
            <p className="simulador__vacio">
              Nadie coincide con eso en el brazo, ni por nombre ni por dirección. ⛔ Eso no es «el
              padrón está vacío»: es esta búsqueda, sobre este brazo. ⚠️ Y antes de descartar:{" "}
              <strong>el nombre busca desde el principio</strong> —un pedazo del medio no
              matchea— y <strong>la dirección tiene que ir completa</strong>.
            </p>
          )}

          {filas.length > 0 && (
            <>
              <ul className="sim__resultados">
                {filas.map((f) => (
                  <li key={f.id}>
                    <button
                      type="button"
                      className="sim__resultado"
                      onClick={() =>
                        onElegir({
                          id: f.id,
                          // ⛔ La dirección va TAL CUAL vino del padrón y no se
                          //    normaliza: el motor corta con `emisor-desconocido`
                          //    ANTES de persistir si no coincide, y ese corte se
                          //    leería como un defecto del padrón.
                          direccion: f.telefono,
                          nombre: f.nombre,
                          // El padrón no dice de qué brazo del motor es nadie.
                          brazo: null,
                        })
                      }
                    >
                      <span className="sim__resultado-nombre">{f.nombre}</span>
                      <code className="sim__resultado-dir">{f.telefono}</code>
                    </button>
                  </li>
                ))}
              </ul>
              <p className="sim__ayuda">
                {/*
                  ⚠️ Se dice cuántos se MUESTRAN y no cuántos hay: con dos consultas
                  hay dos `total`, y sumarlos contaría dos veces a quien matchea por
                  los dos campos. `ADR-0228`.
                */}
                Se muestran {filas.length}
                {filas.length === TOPE_VISIBLE ? " (el tope): afiná la búsqueda para ver el resto" : ""}.
              </p>
            </>
          )}
        </>
      )}

      {/*
        ⛔⭐ RASTRO `[28/8/2026]` — acá decía «sale del padrón. Si no está enrolada en el
        brazo del canal, el motor corta con `emisor-desconocido`». La primera mitad
        describía un solo camino y la segunda daba a entender que el motor mira el
        padrón. `ADR-0232`: el motor mira `F7_PARTICIPANTS`, no el padrón.
      */}
      <p className="sim__ayuda">
        No da de alta a nadie y no inventa ninguna dirección. El motor resuelve al emisor contra
        los participantes que la instancia le declaró —<strong>no contra el padrón</strong>—, así
        que una dirección del padrón que no esté entre ellos produce{" "}
        <code>emisor-desconocido</code>. Eso es el motor diciendo la verdad, no un defecto.
      </p>
    </section>
  )
}
